import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Tag, X } from 'lucide-react';
import { regulatoryTags } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-18 (eISF spec §functional cap #5) — Regulatory Tags admin page.
 *
 * Tenant-scoped vocabulary for the eISF tagging system. Site Admin / Reg
 * Coord mint tenant tags; platform_admin can mint globals (tenant_id =
 * '__global__'). Tags soft-delete via `is_active=false` — they never
 * disappear from the audit trail.
 */

export default function RegulatoryTagsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const listQ = useQuery({
    queryKey: ['regulatory-tags'],
    queryFn: regulatoryTags.list,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Regulatory tags"
        subtitle="Configurable vocabulary for tagging documents. Tenant tags are yours; platform globals appear here too but can't be edited."
        actions={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            New tag
          </button>
        }
      />

      <div className="rounded border border-surface-border bg-white overflow-hidden">
        {listQ.isLoading ? (
          <div className="p-10 text-center text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
          </div>
        ) : !listQ.data?.tags.length ? (
          <div className="p-10 text-center text-sm text-ink-muted">
            No tags yet. Create one to start tagging documents.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
              <tr>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Scope</th>
                <th className="px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data.tags.map((t) => (
                <tr key={t.tag_id} className="border-b border-surface-border/30">
                  <td className="px-4 py-2 font-mono text-xs">{t.code}</td>
                  <td className="px-4 py-2 font-medium text-ink-primary">{t.label}</td>
                  <td className="px-4 py-2 text-xs">{t.category}</td>
                  <td className="px-4 py-2 text-xs">
                    {t.tenant_id === '__global__'
                      ? <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">Global</span>
                      : <span className="inline-flex rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">Tenant</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-ink-muted">{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <NewTagDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['regulatory-tags'] });
          }}
        />
      )}
    </div>
  );
}

function NewTagDialog({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('regulatory');

  const isValid = /^[a-z0-9][a-z0-9-]*$/.test(code) && label.trim().length > 0 && category.trim().length > 0;

  const create = useMutation({
    mutationFn: () => regulatoryTags.create({ code, label: label.trim(), category: category.trim() }),
    onSuccess: (r) => {
      toast.success(`Created tag "${r.tag.label}"`);
      onCreated();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to create tag')),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-primary/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold text-ink-primary flex items-center gap-2">
            <Tag className="h-4 w-4" /> New regulatory tag
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label text-xs">Code (kebab-case)</label>
          <input
            className="input font-mono"
            value={code}
            placeholder="irb-correspondence"
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            maxLength={80}
          />
          {code && !/^[a-z0-9][a-z0-9-]*$/.test(code) && (
            <p className="mt-1 text-[11px] text-danger-700">Lowercase letters, digits, and dashes only.</p>
          )}
        </div>

        <div>
          <label className="label text-xs">Display label</label>
          <input
            className="input"
            value={label}
            placeholder="IRB Correspondence"
            onChange={(e) => setLabel(e.target.value)}
            maxLength={200}
          />
        </div>

        <div>
          <label className="label text-xs">Category</label>
          <input
            className="input"
            value={category}
            list="tag-category-options"
            onChange={(e) => setCategory(e.target.value)}
            maxLength={80}
          />
          <datalist id="tag-category-options">
            <option value="regulatory" />
            <option value="study" />
            <option value="safety" />
            <option value="training" />
            <option value="monitoring" />
            <option value="finance" />
          </datalist>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={create.isPending}>Cancel</button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!isValid || create.isPending}
            className="btn-primary disabled:opacity-60"
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
